'use client'

import React, { createContext, useContext, useReducer, useEffect } from 'react'
import {
  DEFAULT_SLIPPAGE_PERCENTAGE,
  SGP_BET_MAX_MARKETS,
} from '@/Overtime/constants/markets'
import { LOCAL_STORAGE_KEYS } from '@/Overtime/constants/storage'
import { TicketErrorCode } from '@/Overtime/enums/markets'
import { Network } from '@/Overtime/enums/network'
import { isFuturesMarket, isPlayerPropsMarket } from 'overtime-utils'
import { localStore } from 'thales-utils'
import type {
  ParlayPayment,
  SerializableSportMarket,
  TicketPosition,
  TicketRequest,
} from '@/Overtime/types/markets'
import type { TicketError } from '@/Overtime/types/tickets'
import {
  isPlayerPropsCombiningEnabled,
  isSameMarket,
  serializableSportMarketAsSportMarket,
} from '@/Overtime/utils/marketsV2'
import { useChainId } from 'wagmi'
import { constants } from '@/helpers'

const DEFAULT_MAX_TICKET_SIZE = 10

type TicketRequestData = TicketRequest & {
  timestamp: number
}

type TicketState = {
  ticketsByChainId: Record<number, TicketPosition[]>
  currentChainId: number | null
  ticketRequestsById: Record<string, TicketRequestData>
  payment: ParlayPayment
  maxTicketSize: number
  liveBetSlippage: number
  isFreeBetDisabledByUser: boolean
  isSystemBet: boolean
  isSgp: boolean
  error: TicketError
}

type TicketAction =
  | { type: 'UPDATE_TICKET'; payload: TicketPosition }
  | { type: 'SET_MAX_TICKET_SIZE'; payload: number }
  | {
      type: 'REMOVE_FROM_TICKET'
      payload: SerializableSportMarket | TicketPosition
    }
  | { type: 'REMOVE_ALL' }
  | { type: 'UPDATE_TICKET_REQUESTS'; payload: TicketRequest }
  | { type: 'REMOVE_TICKET_REQUEST_BY_ID'; payload: string }
  | { type: 'REMOVE_TICKET_REQUESTS' }
  | { type: 'SET_LIVE_BET_SLIPPAGE'; payload: number }
  | {
      type: 'SET_PAYMENT_SELECTED_COLLATERAL_INDEX'
      payload: {
        selectedCollateralIndex: number
        networkId: Network
        forcedChange?: boolean
      }
    }
  | { type: 'SET_PAYMENT_AMOUNT_TO_BUY'; payload: number | string }
  | { type: 'SET_IS_FREE_BET_DISABLED_BY_USER'; payload: boolean }
  | { type: 'SET_IS_SYSTEM_BET'; payload: boolean }
  | { type: 'SET_IS_SGP'; payload: boolean }
  | { type: 'SET_TICKET_ERROR'; payload: TicketError }
  | { type: 'RESET_TICKET_ERROR' }
  | { type: 'SET_CURRENT_CHAIN_ID'; payload: number }

const getDefaultTicketsByChainId = (): Record<number, TicketPosition[]> => {
  const lsParlay = localStore.get(LOCAL_STORAGE_KEYS.PARLAY)
  if (lsParlay === undefined) {
    return {}
  }

  const tickets = lsParlay as TicketPosition[]

  // Group tickets by chainId
  const ticketsByChainId: Record<number, TicketPosition[]> = {}
  tickets.forEach(ticket => {
    if (!ticketsByChainId[ticket.chainId]) {
      ticketsByChainId[ticket.chainId] = []
    }
    ticketsByChainId[ticket.chainId].push(ticket)
  })

  return ticketsByChainId
}

const getDefaultPayment = (): ParlayPayment => {
  const lsForceCollateralChange = localStore.get(
    LOCAL_STORAGE_KEYS.COLLATERAL_CHANGED
  )

  return {
    selectedCollateralIndex: 0,
    amountToBuy: '',
    networkId: Network.OptimismMainnet,
    forceChangeCollateral:
      lsForceCollateralChange !== undefined
        ? (lsForceCollateralChange as boolean)
        : false,
  }
}

const getDefaultLiveSlippage = (): number => {
  const slippage = localStore.get<number>(constants.localStorageKeys.slippage)
  return slippage !== undefined ? slippage : DEFAULT_SLIPPAGE_PERCENTAGE
}

const getDefaultIsSystemBet = (): boolean => {
  const lsIsSystemBet = localStore.get(LOCAL_STORAGE_KEYS.IS_SYSTEM_BET)
  return lsIsSystemBet !== undefined ? (lsIsSystemBet as boolean) : false
}

const getDefaultIsSgp = (): boolean => {
  const lsIsSgp = localStore.get(LOCAL_STORAGE_KEYS.IS_SGP)
  return lsIsSgp !== undefined ? (lsIsSgp as boolean) : false
}

const getDefaultError = (): TicketError => {
  return { code: TicketErrorCode.NO_ERROS, data: '' }
}

const initialState: TicketState = {
  ticketsByChainId: getDefaultTicketsByChainId(),
  currentChainId: null,
  ticketRequestsById: {},
  payment: getDefaultPayment(),
  maxTicketSize: DEFAULT_MAX_TICKET_SIZE,
  liveBetSlippage: getDefaultLiveSlippage(),
  isFreeBetDisabledByUser: false,
  isSystemBet: getDefaultIsSystemBet(),
  isSgp: getDefaultIsSgp(),
  error: getDefaultError(),
}

const ticketReducer = (
  state: TicketState,
  action: TicketAction
): TicketState => {
  const currentTickets = state.currentChainId
    ? state.ticketsByChainId[state.currentChainId] || []
    : []

  switch (action.type) {
    case 'UPDATE_TICKET': {
      if (!state.currentChainId) {
        return state
      }

      const ticketCopy = [...currentTickets]
      const existingGameIdIndex = currentTickets.findIndex(
        el => el.gameId === action.payload.gameId
      )

      if (action.payload.live) {
        const newTicketsByChainId = {
          ...state.ticketsByChainId,
          [state.currentChainId]: [action.payload],
        }
        return { ...state, ticketsByChainId: newTicketsByChainId }
      }

      if (state.isSgp && currentTickets.length) {
        if (currentTickets.length < SGP_BET_MAX_MARKETS) {
          const isDifferentGame =
            currentTickets[0].gameId !== action.payload.gameId
          if (isDifferentGame) {
            return {
              ...state,
              error: { code: TicketErrorCode.SGP_DIFFERENT_GAME, data: '' },
            }
          }
          const existingPositionIndex = currentTickets.findIndex(el =>
            isSameMarket(el, action.payload, true)
          )
          if (existingPositionIndex === -1) {
            const newTicketsByChainId = {
              ...state.ticketsByChainId,
              [state.currentChainId]: [...currentTickets, action.payload],
            }
            return { ...state, ticketsByChainId: newTicketsByChainId }
          }
          ticketCopy[existingPositionIndex] = action.payload
          const newTicketsByChainId = {
            ...state.ticketsByChainId,
            [state.currentChainId]: [...ticketCopy],
          }
          return { ...state, ticketsByChainId: newTicketsByChainId }
        }
        return {
          ...state,
          error: {
            code: TicketErrorCode.SGP_MAX_MARKETS,
            data: SGP_BET_MAX_MARKETS.toString(),
          },
        }
      }

      if (existingGameIdIndex === -1) {
        if (currentTickets.length >= state.maxTicketSize) {
          return {
            ...state,
            error: {
              code: TicketErrorCode.MAX_MATCHES,
              data: state.maxTicketSize.toString(),
            },
          }
        }

        if (
          currentTickets.length > 0 &&
          (isFuturesMarket(action.payload.typeId) ||
            currentTickets.some(position => isFuturesMarket(position.typeId)))
        ) {
          return {
            ...state,
            error: {
              code: TicketErrorCode.FUTURES_COMBINING_NOT_SUPPORTED,
              data: '',
            },
          }
        }

        const newTicketsByChainId = {
          ...state.ticketsByChainId,
          [state.currentChainId]: [...currentTickets, action.payload],
        }
        return { ...state, ticketsByChainId: newTicketsByChainId }
      }

      const existingPosition = currentTickets[existingGameIdIndex]
      const isExistingPositionPP = isPlayerPropsMarket(existingPosition.typeId)
      const isNewPositionPP = isPlayerPropsMarket(action.payload.typeId)

      if (
        ((isExistingPositionPP && !isNewPositionPP) ||
          (!isExistingPositionPP && isNewPositionPP)) &&
        isPlayerPropsCombiningEnabled(action.payload.leagueId)
      ) {
        return {
          ...state,
          error: {
            code: TicketErrorCode.OTHER_TYPES_WITH_PLAYER_PROPS,
            data: '',
          },
        }
      }

      if (
        isExistingPositionPP &&
        isNewPositionPP &&
        isPlayerPropsCombiningEnabled(action.payload.leagueId)
      ) {
        const playerAlreadyOnTicketIndex = currentTickets.findIndex(
          el =>
            el.playerId === action.payload.playerId &&
            action.payload.playerId > 0
        )

        if (playerAlreadyOnTicketIndex > -1) {
          if (
            currentTickets[playerAlreadyOnTicketIndex].typeId !==
            action.payload.typeId
          ) {
            return {
              ...state,
              error: {
                code: TicketErrorCode.SAME_PLAYER_DIFFERENT_TYPES,
                data: action.payload.playerName,
              },
            }
          }
          ticketCopy[playerAlreadyOnTicketIndex] = action.payload
          const newTicketsByChainId = {
            ...state.ticketsByChainId,
            [state.currentChainId]: [...ticketCopy],
          }
          return { ...state, ticketsByChainId: newTicketsByChainId }
        }

        if (currentTickets.length < state.maxTicketSize) {
          const newTicketsByChainId = {
            ...state.ticketsByChainId,
            [state.currentChainId]: [...currentTickets, action.payload],
          }
          return { ...state, ticketsByChainId: newTicketsByChainId }
        }
        return {
          ...state,
          error: {
            code: TicketErrorCode.MAX_MATCHES,
            data: state.maxTicketSize.toString(),
          },
        }
      }

      ticketCopy[existingGameIdIndex] = action.payload
      const newTicketsByChainId = {
        ...state.ticketsByChainId,
        [state.currentChainId]: [...ticketCopy],
      }
      return { ...state, ticketsByChainId: newTicketsByChainId }
    }

    case 'SET_MAX_TICKET_SIZE':
      return { ...state, maxTicketSize: action.payload }

    case 'REMOVE_FROM_TICKET': {
      if (!state.currentChainId) {
        return state
      }

      let payload
      if (action.payload.hasOwnProperty('maturity')) {
        payload = serializableSportMarketAsSportMarket(
          action.payload as SerializableSportMarket
        )
      } else {
        payload = action.payload as TicketPosition
      }

      const newTickets = currentTickets.filter(
        market => !isSameMarket(payload, market, state.isSgp)
      )

      const newTicketsByChainId = {
        ...state.ticketsByChainId,
        [state.currentChainId]: newTickets,
      }

      return {
        ...state,
        ticketsByChainId: newTicketsByChainId,
        payment:
          newTickets.length === 0
            ? { ...state.payment, amountToBuy: '' }
            : state.payment,
        error: getDefaultError(),
      }
    }

    case 'REMOVE_ALL': {
      if (!state.currentChainId) {
        return state
      }

      const newTicketsByChainId = {
        ...state.ticketsByChainId,
        [state.currentChainId]: [],
      }

      return {
        ...state,
        ticketsByChainId: newTicketsByChainId,
        payment: { ...state.payment, amountToBuy: '' },
        error: getDefaultError(),
      }
    }

    case 'SET_CURRENT_CHAIN_ID': {
      return {
        ...state,
        currentChainId: action.payload,
        error: getDefaultError(),
      }
    }

    case 'UPDATE_TICKET_REQUESTS': {
      const payloadTicketRequest = action.payload
      let requestId = payloadTicketRequest.requestId
      if (requestId) {
        const { [payloadTicketRequest.initialRequestId]: _, ...rest } =
          state.ticketRequestsById
        state.ticketRequestsById = rest
      } else {
        requestId = payloadTicketRequest.initialRequestId
      }

      const ticketRequestData: TicketRequestData = {
        ...payloadTicketRequest,
        requestId,
        timestamp: !state.ticketRequestsById[requestId]?.timestamp
          ? Date.now()
          : state.ticketRequestsById[requestId].timestamp,
      }

      return {
        ...state,
        ticketRequestsById: {
          ...state.ticketRequestsById,
          [requestId]: ticketRequestData,
        },
      }
    }

    case 'REMOVE_TICKET_REQUEST_BY_ID': {
      const { [action.payload]: _, ...rest } = state.ticketRequestsById
      return { ...state, ticketRequestsById: rest }
    }

    case 'REMOVE_TICKET_REQUESTS':
      return { ...state, ticketRequestsById: {} }

    case 'SET_LIVE_BET_SLIPPAGE':
      return { ...state, liveBetSlippage: action.payload }

    case 'SET_PAYMENT_SELECTED_COLLATERAL_INDEX':
      return {
        ...state,
        payment: {
          ...state.payment,
          selectedCollateralIndex: action.payload.selectedCollateralIndex,
          networkId: action.payload.networkId,
          forceChangeCollateral: action.payload.forcedChange
            ? true
            : state.payment.forceChangeCollateral,
        },
      }

    case 'SET_PAYMENT_AMOUNT_TO_BUY':
      return {
        ...state,
        payment: { ...state.payment, amountToBuy: action.payload },
      }

    case 'SET_IS_FREE_BET_DISABLED_BY_USER':
      return { ...state, isFreeBetDisabledByUser: action.payload }

    case 'SET_IS_SYSTEM_BET':
      return { ...state, isSystemBet: action.payload }

    case 'SET_IS_SGP': {
      if (
        state.isSgp &&
        state.currentChainId &&
        state.ticketsByChainId[state.currentChainId]?.length > 1
      ) {
        const currentTickets = state.ticketsByChainId[state.currentChainId]
        const firstTicketGameId = currentTickets[0].gameId
        const isDifferentGame = currentTickets.some(
          ticket => ticket.gameId !== firstTicketGameId
        )
        if (isDifferentGame) {
          return {
            ...state,
            isSgp: action.payload,
            ticketsByChainId: {
              ...state.ticketsByChainId,
              [state.currentChainId]: [],
            },
            payment: { ...state.payment, amountToBuy: '' },
            error: getDefaultError(),
          }
        }
      }
      return { ...state, isSgp: action.payload }
    }

    case 'SET_TICKET_ERROR':
      return { ...state, error: action.payload }

    case 'RESET_TICKET_ERROR':
      return { ...state, error: getDefaultError() }

    default:
      return state
  }
}

type TicketContextType = {
  state: TicketState
  ticket: TicketPosition[] // Current chain's tickets
  updateTicket: (position: TicketPosition) => void
  setMaxTicketSize: (size: number) => void
  removeFromTicket: (market: SerializableSportMarket | TicketPosition) => void
  removeAll: (callback?: () => void) => void
  updateTicketRequests: (request: TicketRequest) => void
  removeTicketRequestById: (id: string) => void
  removeTicketRequests: () => void
  setLiveBetSlippage: (slippage: number) => void
  setPaymentSelectedCollateralIndex: (payload: {
    selectedCollateralIndex: number
    networkId: Network
    forcedChange?: boolean
  }) => void
  setPaymentAmountToBuy: (amount: number | string) => void
  setIsFreeBetDisabledByUser: (disabled: boolean) => void
  setIsSystemBet: (isSystemBet: boolean) => void
  setIsSgp: (isSgp: boolean) => void
  setTicketError: (error: TicketError) => void
  resetTicketError: () => void
  setCurrentChainId: (chainId: number) => void
}

const TicketContext = createContext<TicketContextType | undefined>(undefined)

export const TicketProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const chainId = useChainId()
  const [state, dispatch] = useReducer(ticketReducer, initialState)

  // Set current chainId when it changes
  useEffect(() => {
    if (chainId) {
      dispatch({ type: 'SET_CURRENT_CHAIN_ID', payload: chainId })
    }
  }, [chainId])

  // Save all tickets to localStorage whenever any chain's tickets change
  useEffect(() => {
    const allTickets: TicketPosition[] = []
    Object.values(state.ticketsByChainId).forEach(tickets => {
      allTickets.push(...tickets)
    })
    localStore.set(LOCAL_STORAGE_KEYS.PARLAY, allTickets)
  }, [state.ticketsByChainId])

  // useEffect(() => {
  //   localStore.set(LOCAL_STORAGE_KEYS.LIVE_BET_SLIPPAGE, state.liveBetSlippage)
  // }, [state.liveBetSlippage])

  useEffect(() => {
    localStore.set(LOCAL_STORAGE_KEYS.IS_SYSTEM_BET, state.isSystemBet)
  }, [state.isSystemBet])

  useEffect(() => {
    localStore.set(LOCAL_STORAGE_KEYS.IS_SGP, state.isSgp)
  }, [state.isSgp])

  useEffect(() => {
    if (state.payment.forceChangeCollateral) {
      localStore.set(LOCAL_STORAGE_KEYS.COLLATERAL_CHANGED, true)
    }
  }, [state.payment.forceChangeCollateral])

  const value = {
    state,
    ticket: state.currentChainId
      ? state.ticketsByChainId[state.currentChainId] || []
      : [],
    updateTicket: (position: TicketPosition) =>
      dispatch({ type: 'UPDATE_TICKET', payload: position }),
    setMaxTicketSize: (size: number) =>
      dispatch({ type: 'SET_MAX_TICKET_SIZE', payload: size }),
    removeFromTicket: (market: SerializableSportMarket | TicketPosition) =>
      dispatch({ type: 'REMOVE_FROM_TICKET', payload: market }),
    removeAll: (callback?: () => void) => {
      dispatch({ type: 'REMOVE_ALL' })
      if (callback) {
        callback()
      }
    },
    updateTicketRequests: (request: TicketRequest) =>
      dispatch({ type: 'UPDATE_TICKET_REQUESTS', payload: request }),
    removeTicketRequestById: (id: string) =>
      dispatch({ type: 'REMOVE_TICKET_REQUEST_BY_ID', payload: id }),
    removeTicketRequests: () => dispatch({ type: 'REMOVE_TICKET_REQUESTS' }),
    setLiveBetSlippage: (slippage: number) =>
      dispatch({ type: 'SET_LIVE_BET_SLIPPAGE', payload: slippage }),
    setPaymentSelectedCollateralIndex: (payload: {
      selectedCollateralIndex: number
      networkId: Network
      forcedChange?: boolean
    }) => dispatch({ type: 'SET_PAYMENT_SELECTED_COLLATERAL_INDEX', payload }),
    setPaymentAmountToBuy: (amount: number | string) =>
      dispatch({ type: 'SET_PAYMENT_AMOUNT_TO_BUY', payload: amount }),
    setIsFreeBetDisabledByUser: (disabled: boolean) =>
      dispatch({ type: 'SET_IS_FREE_BET_DISABLED_BY_USER', payload: disabled }),
    setIsSystemBet: (isSystemBet: boolean) =>
      dispatch({ type: 'SET_IS_SYSTEM_BET', payload: isSystemBet }),
    setIsSgp: (isSgp: boolean) =>
      dispatch({ type: 'SET_IS_SGP', payload: isSgp }),
    setTicketError: (error: TicketError) =>
      dispatch({ type: 'SET_TICKET_ERROR', payload: error }),
    resetTicketError: () => dispatch({ type: 'RESET_TICKET_ERROR' }),
    setCurrentChainId: (chainId: number) =>
      dispatch({ type: 'SET_CURRENT_CHAIN_ID', payload: chainId }),
  }

  return (
    <TicketContext.Provider value={value}>{children}</TicketContext.Provider>
  )
}

export const useTicket = () => {
  const context = useContext(TicketContext)
  if (context === undefined) {
    throw new Error('useTicket must be used within a TicketProvider')
  }
  return context
}
